import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import { DESTINATIONS } from "../../data/destinations";
import { DestinationHero } from "../destinations-editorial/DestinationHero";
import { FloatingSectionNav } from "../destinations-editorial/FloatingSectionNav";
import { DestinationIntro } from "../destinations-editorial/DestinationIntro";
import { DestinationHighlights } from "../destinations-editorial/DestinationHighlights";
import { ItinerarySection } from "../destinations-editorial/ItinerarySection";
import { WhereToStay } from "../destinations-editorial/WhereToStay";
import { FeaturedStays } from "../destinations-editorial/FeaturedStays";
import { TravelTips } from "../destinations-editorial/TravelTips";
import { DestinationCTA } from "../destinations-editorial/DestinationCTA";
import { NearbyDestinations } from "../destinations-editorial/NearbyDestinations";
import { FAQSection } from "../destinations-editorial/FAQSection";

const ORANGE = "#E8470A";
const PAGE_BG = "#F5F2EC";

const DestinationPage = ({ data }) => {
  const {
    h1, intro, searchUrl, name, heroImage, introImage, ctaImage,
    sectionNavigation = [],
    highlights = [],
    highlightsSection,
    introParagraphs = [],
    itinerary3Days = [],
    itinerary5Days = [],
    areas = [],
    stays = [],
    tips = [],
    sections = [],
    faqs = [],
    nearbyDestinations: nearbyRaw = null,
    relatedDestinations = [],
  } = data;

  // Map relatedDestinations → NearbyDestinations format
  const nearbyDestinations = (nearbyRaw || relatedDestinations).map((d) => {
    const dest = DESTINATIONS[d.slug] || {};
    return {
      name: d.name,
      href: `/destinos/${d.slug}`,
      image: d.image || dest.heroImage || null,
      description: d.description || (dest.intro ? dest.intro.slice(0, 100) + "…" : ""),
      distance: d.distance || "",
    };
  });

  return (
    <main style={{ backgroundColor: PAGE_BG, paddingBottom: "80px" }}>

      {/* HERO */}
      <DestinationHero
        destination={name || h1}
        description={intro}
        heroImage={heroImage}
      />

      {/* FLOATING NAV */}
      {sectionNavigation.length > 0 && (
        <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
          <FloatingSectionNav sections={sectionNavigation} />
        </div>
      )}

      {/* INTRO */}
      {introParagraphs.length > 0 && introImage && (
        <DestinationIntro destination={name} introImage={introImage} paragraphs={introParagraphs} />
      )}

      {/* HIGHLIGHTS */}
      {highlights.length > 0 && (
        <DestinationHighlights
          title={highlightsSection?.title || `Lo mejor de viajar a ${name}`}
          description={
            highlightsSection?.description ||
            `${name} combina naturaleza, experiencias y propuestas para distintos tipos de viaje durante todo el año.`
          }
          highlights={highlights}
        />
      )}

      {/* ITINERARIOS */}
      {itinerary3Days.length > 0 && (
        <ItinerarySection destination={name} days={itinerary3Days} label="3 días" sectionId="itinerary-3" />
      )}
      {itinerary5Days.length > 0 && (
        <ItinerarySection destination={name} days={itinerary5Days} label="5 días" sectionId="itinerary-5" />
      )}

      {/* ZONAS */}
      {areas.length > 0 && <WhereToStay destination={name} areas={areas} />}

      {/* ALOJAMIENTOS */}
      {stays.length > 0 && <FeaturedStays destination={name} stays={stays} />}

      {/* TIPS */}
      {tips.length > 0 && <TravelTips destination={name} tips={tips} />}

      {/* ── BLOQUE INFERIOR ── */}
      <div style={{ maxWidth: "1140px", margin: "0 auto", padding: "64px 24px 0" }}>

        {/* SECCIONES DE CONTENIDO */}
        {sections.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "40px" }}>
            {sections.map((section, idx) => (
              <motion.article
                key={section.title}
                id={section.id || undefined}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.08, ease: "easeOut" }}
                viewport={{ once: true, margin: "-60px" }}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  padding: "32px 36px",
                  border: "1px solid #ede8df",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <h2 style={{
                  fontSize: "22px",
                  fontWeight: 700,
                  color: "#111827",
                  margin: "0 0 16px",
                  lineHeight: 1.3,
                }}>
                  {section.title}
                </h2>

                {section.paragraphs && section.paragraphs.map((p, i) => (
                  <p key={i} style={{
                    fontSize: "15px",
                    lineHeight: 1.75,
                    color: "#4B5563",
                    margin: i === 0 ? "0" : "12px 0 0",
                  }}>
                    {p}
                  </p>
                ))}

                {section.bullets && section.bullets.length > 0 && (
                  <ul style={{ margin: "16px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "10px" }}>
                    {section.bullets.map((b, i) => (
                      <li key={i} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                        <span style={{
                          flexShrink: 0,
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: ORANGE,
                          marginTop: "8px",
                        }} />
                        <span style={{ fontSize: "15px", lineHeight: 1.75, color: "#4B5563" }}>
                          {b}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.article>
            ))}
          </div>
        )}

        {/* CTA */}
        {ctaImage ? (
          <div style={{ marginBottom: "40px" }}>
            <DestinationCTA destination={name} ctaImage={ctaImage} />
          </div>
        ) : (
          <motion.section
            id="alojamientos"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            viewport={{ once: true, margin: "-60px" }}
            style={{
              backgroundColor: "#172033",
              borderRadius: "16px",
              padding: "40px 44px",
              marginBottom: "40px",
            }}
          >
            <span style={{
              display: "block",
              color: "#96F9FF",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              marginBottom: "14px",
            }}>
              Buscar en Alohar
            </span>
            <h2 style={{
              fontSize: "clamp(20px, 2.5vw, 28px)",
              fontWeight: 700,
              color: "#ffffff",
              margin: "0 0 14px",
              lineHeight: 1.3,
            }}>
              Encontrá tu alojamiento temporario en {name}
            </h2>
            <p style={{
              fontSize: "15px",
              lineHeight: 1.7,
              color: "#94a3b8",
              margin: "0 0 28px",
              maxWidth: "560px",
            }}>
              Usá el buscador de Alohar para explorar departamentos, casas y cabañas
              disponibles en {name}. Filtrá por fechas, capacidad y zona para encontrar
              la opción que se ajuste a tu viaje.
            </p>
            <Link
              to={searchUrl}
              style={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: ORANGE,
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "15px",
                padding: "13px 28px",
                borderRadius: "999px",
                textDecoration: "none",
                boxShadow: "0 4px 16px rgba(232,71,10,0.35)",
              }}
            >
              Ver alojamientos disponibles en {name} →
            </Link>
          </motion.section>
        )}

        {/* DESTINOS CERCANOS */}
        {nearbyDestinations.length > 0 && (
          <NearbyDestinations destinations={nearbyDestinations} />
        )}

        {/* FAQ */}
        {faqs.length > 0 && (
          <FAQSection destination={name} faqs={faqs} />
        )}

        {/* LINKS ÚTILES */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          viewport={{ once: true, margin: "-60px" }}
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            padding: "32px 36px",
            border: "1px solid #ede8df",
            marginTop: "40px",
          }}
        >
          <h2 style={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#111827",
            margin: "0 0 20px",
          }}>
            Más información útil para tu viaje
          </h2>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: "12px",
          }}>
            <Link to="/ayuda" style={{ textDecoration: "none" }}>
              <div style={{
                backgroundColor: "#EDF6FF",
                borderRadius: "12px",
                padding: "16px 20px",
                transition: "background-color 0.2s",
              }}>
                <p style={{ fontSize: "15px", fontWeight: 600, color: "#111827", margin: "0 0 4px" }}>
                  Centro de Ayuda
                </p>
                <p style={{ fontSize: "13px", color: "#6B7280", margin: 0 }}>
                  Reservas, pagos y plataforma →
                </p>
              </div>
            </Link>

            <Link to="/quienes-somos" style={{ textDecoration: "none" }}>
              <div style={{
                backgroundColor: "#F7F0FF",
                borderRadius: "12px",
                padding: "16px 20px",
              }}>
                <p style={{ fontSize: "15px", fontWeight: 600, color: "#111827", margin: "0 0 4px" }}>
                  Quiénes somos
                </p>
                <p style={{ fontSize: "13px", color: "#6B7280", margin: 0 }}>
                  Qué es Alohar →
                </p>
              </div>
            </Link>

            <Link to={searchUrl} style={{ textDecoration: "none" }}>
              <div style={{
                backgroundColor: "#FFF4EB",
                borderRadius: "12px",
                padding: "16px 20px",
              }}>
                <p style={{ fontSize: "15px", fontWeight: 600, color: "#111827", margin: "0 0 4px" }}>
                  Alojamientos en {name}
                </p>
                <p style={{ fontSize: "13px", color: "#6B7280", margin: 0 }}>
                  Explorar opciones disponibles →
                </p>
              </div>
            </Link>
          </div>
        </motion.section>

      </div>
    </main>
  );
};

export default DestinationPage;
